import { useState, useEffect, useMemo } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBulkLocks } from '../hooks/useBulkLocks';
import { LockIndicator } from '../components/permissions/LockIndicator';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Calendar,
    Search,
    Star,
    Loader2,
    ArrowLeft,
    Eye,
    Trash2,
    User,
    Monitor,
    MapPin,
    CalendarDays,
    Clock,
    AlertTriangle,
    FileQuestion,
    X,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { VisiteDetailDialog } from '../components/visites/VisiteDetailDialog';
import { VisiteHistoryDialog } from '../components/visites/VisiteHistoryDialog';
import { ReadOnlyGuard } from '../components/permissions/ReadOnlyGuard';
import { listsEqualById } from '../lib/listDiff';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
    a_venir: 'À venir',
    commencee: 'Commencée',
    terminee: 'Terminée',
    decalee: 'À reprogrammer',
    annulee: 'Annulée',
};

const STATUS_COLORS = {
    a_venir: 'bg-blue-100 text-blue-700 border-blue-200',
    commencee: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    terminee: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    decalee: 'bg-orange-100 text-orange-700 border-orange-200',
    annulee: 'bg-red-100 text-red-700 border-red-200',
};

function formatDateFr(dateStr) {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return dateStr;
    }
}

export default function VisitesListePage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [visites, setVisites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [config, setConfig] = useState(null);

    const [detailVisite, setDetailVisite] = useState(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    const [historyVisite, setHistoryVisite] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    // Anomalies (visites restees nouveau >30min OU passees sans inventaire)
    const [anomalyStats, setAnomalyStats] = useState({ non_traitee: 0, sans_inventaire: 0 });
    const [activeAnomalyView, setActiveAnomalyView] = useState(null); // 'non_traitee' | 'sans_inventaire'
    const [anomalyList, setAnomalyList] = useState([]);
    const [loadingAnomaly, setLoadingAnomaly] = useState(false);

    const fetchAnomalyStats = async () => {
        try {
            const { data } = await axios.get(`${API}/api/visites/anomalies/stats`, { withCredentials: true });
            setAnomalyStats(data || { non_traitee: 0, sans_inventaire: 0 });
        } catch { /* silent */ }
    };

    const openAnomalyView = async (kind) => {
        if (activeAnomalyView === kind) {
            setActiveAnomalyView(null);
            setAnomalyList([]);
            return;
        }
        setActiveAnomalyView(kind);
        setLoadingAnomaly(true);
        try {
            const { data } = await axios.get(`${API}/api/visites/anomalies/list?kind=${kind}`, { withCredentials: true });
            setAnomalyList(data || []);
        } catch {
            setAnomalyList([]);
        } finally {
            setLoadingAnomaly(false);
        }
    };

    const fetchVisites = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            // Cache-buster `?_t=` (Safari/iPad cache les GET trop agressivement)
            const { data } = await axios.get(`${API}/api/visites?_t=${Date.now()}`, { withCredentials: true });
            const next = data || [];
            setVisites(prev => listsEqualById(prev, next) ? prev : next);
            // Synchronise la visite affichee dans la modale avec la derniere version
            // recue du serveur (sinon le Select affiche encore l'ancien statut).
            setDetailVisite(prev => {
                if (!prev?.id) return prev;
                const fresh = next.find(v => v.id === prev.id);
                return fresh ? { ...prev, ...fresh } : prev;
            });
        } catch {
            if (!silent) setVisites([]);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchVisites();
        fetchAnomalyStats();
        axios.get(`${API}/api/visites/config`, { withCredentials: true })
            .then(({ data }) => setConfig(data))
            .catch(() => setConfig(null));
    }, []);

    useAutoRefresh(() => { fetchVisites(true); fetchAnomalyStats(); });

    const visiteLocks = useBulkLocks('visite', (visites || []).map(v => v.id));

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return visites.filter(v => {
            if (statusFilter !== 'all' && v.statut !== statusFilter) return false;
            if (!q) return true;
            return (
                (v.prospect_nom || '').toLowerCase().includes(q)
                || (v.prospect_telephone || '').toLowerCase().includes(q)
                || (v.commercial_name || '').toLowerCase().includes(q)
                || (v.date || '').includes(q)
            );
        }).sort((a, b) => {
            // tri : date desc puis heure desc
            const dA = `${a.date || ''} ${a.heure || ''}`;
            const dB = `${b.date || ''} ${b.heure || ''}`;
            return dB.localeCompare(dA);
        });
    }, [visites, search, statusFilter]);

    const openDetail = (v) => { setDetailVisite(v); setIsDetailOpen(true); };

    return (
        <div className="container mx-auto p-6 max-w-7xl" data-testid="visites-liste-page">
            <ReadOnlyGuard module="visites_calendrier">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => navigate('/calendrier')} data-testid="visites-liste-back-btn">
                        <ArrowLeft size={16} className="mr-1" />
                        Retour au calendrier
                    </Button>
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="visites-liste-title">
                        Liste des visites
                    </h1>
                </div>
            </div>

            {/* Cartes d'anomalies — style cartes Prospects */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="visites-anomalies-stats">
                <Card
                    className={`rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white ${activeAnomalyView === 'non_traitee' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-amber-200'}`}
                    onClick={() => openAnomalyView('non_traitee')}
                    data-testid="visites-anomaly-card-non-traitee"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                            <AlertTriangle size={20} className="text-amber-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-amber-700">{anomalyStats.non_traitee}</p>
                            <p className="text-xs text-slate-500">Visites non traitées · statut "Nouveau" depuis &gt; 30 min</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    className={`rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white ${activeAnomalyView === 'sans_inventaire' ? 'border-violet-500 ring-2 ring-violet-200' : 'border-violet-200'}`}
                    onClick={() => openAnomalyView('sans_inventaire')}
                    data-testid="visites-anomaly-card-sans-inventaire"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                            <FileQuestion size={20} className="text-violet-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-violet-700">{anomalyStats.sans_inventaire}</p>
                            <p className="text-xs text-slate-500">Visites passées sans inventaire créé</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Panneau liste anomalies */}
            {activeAnomalyView && (
                <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6" data-testid="visites-anomaly-panel">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                {activeAnomalyView === 'non_traitee'
                                    ? <AlertTriangle size={18} className="text-amber-600" />
                                    : <FileQuestion size={18} className="text-violet-600" />}
                                <p className="text-sm font-semibold text-slate-800">
                                    {activeAnomalyView === 'non_traitee'
                                        ? `Visites non traitées (${anomalyList.length})`
                                        : `Visites passées sans inventaire (${anomalyList.length})`}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setActiveAnomalyView(null); setAnomalyList([]); }}
                                className="p-1 rounded hover:bg-slate-100 text-slate-500"
                                data-testid="visites-anomaly-close-btn"
                                aria-label="Fermer"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        {loadingAnomaly ? (
                            <div className="flex items-center justify-center py-8 text-slate-500">
                                <Loader2 size={18} className="animate-spin mr-2" /> Chargement...
                            </div>
                        ) : anomalyList.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-6">Aucune visite dans cette catégorie 👌</p>
                        ) : (
                            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                                {anomalyList.map(v => (
                                    <button
                                        key={v.id}
                                        type="button"
                                        onClick={() => openDetail({
                                            id: v.id,
                                            prospect_id: v.prospect_id,
                                            prospect_nom: v.prospect_nom,
                                            prospect_telephone: v.prospect_telephone,
                                            date: v.date,
                                            heure: v.heure,
                                            type_visite: v.type_visite,
                                            statut: v.statut,
                                            commercial_id: v.commercial_id,
                                            commercial_name: v.commercial_name,
                                            commercial_couleur: v.commercial_couleur,
                                        })}
                                        className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 transition"
                                        data-testid={`visites-anomaly-item-${v.id}`}
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-sm font-medium text-slate-800 truncate">{v.prospect_nom || 'Client'}</span>
                                            {v.commercial_couleur && (
                                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: v.commercial_couleur }} />
                                            )}
                                            <span className="text-xs text-slate-500 truncate">{v.commercial_name || ''}</span>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 text-xs text-slate-500">
                                            <CalendarDays size={12} />
                                            <span>{formatDateFr(v.date)}</span>
                                            <Clock size={12} />
                                            <span>{v.heure || '-'}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Filters */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Rechercher (nom, téléphone, commercial, date)..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="visites-liste-search-input"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-[220px]" data-testid="visites-liste-status-filter">
                                <SelectValue placeholder="Filtrer par statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {Object.entries(STATUS_LABELS).map(([k, label]) => (
                                    <SelectItem key={k} value={k}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <Calendar size={20} className="text-[#2e7cd6]" />
                        Liste des visites ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            Aucune visite trouvée
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid="visites-liste-table" className="min-w-[820px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead className="w-10"></TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Commercial</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Heure</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((v) => (
                                        <TableRow
                                            key={v.id}
                                            data-testid={`visite-liste-row-${v.id}`}
                                            className="cursor-pointer hover:bg-slate-50"
                                            onClick={() => openDetail(v)}
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-cyan-50 flex items-center justify-center">
                                                        <User size={20} className="text-cyan-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{v.prospect_nom || '—'}</p>
                                                        {v.prospect_telephone && (
                                                            <p className="text-xs text-slate-400">{v.prospect_telephone}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="w-10">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setHistoryVisite(v);
                                                        setIsHistoryOpen(true);
                                                    }}
                                                    className="p-1 rounded-md hover:bg-blue-50 transition-colors"
                                                    title="Historique de la visite"
                                                    data-testid={`visite-history-${v.id}`}
                                                >
                                                    <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                                                </button>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={v.type_visite === 'virtuelle'
                                                    ? 'bg-violet-100 text-violet-700 border-violet-200'
                                                    : 'bg-cyan-100 text-cyan-700 border-cyan-200'}>
                                                    {v.type_visite === 'virtuelle' ? (
                                                        <span className="inline-flex items-center gap-1"><Monitor size={12} /> Virtuelle</span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1"><MapPin size={12} /> Présentielle</span>
                                                    )}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {v.commercial_name ? (
                                                    <Badge
                                                        variant="outline"
                                                        className="border text-xs"
                                                        style={v.commercial_couleur ? {
                                                            backgroundColor: v.commercial_couleur + '1A',
                                                            color: v.commercial_couleur,
                                                            borderColor: v.commercial_couleur + '40',
                                                        } : {}}
                                                    >
                                                        {v.commercial_name}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-slate-400 text-xs italic">Non affecté</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-slate-600">
                                                <span className="inline-flex items-center gap-1">
                                                    <CalendarDays size={13} className="text-slate-400" />
                                                    {formatDateFr(v.date)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-slate-600">
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock size={13} className="text-slate-400" />
                                                    {v.heure || '-'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={`${STATUS_COLORS[v.statut] || 'bg-slate-100 text-slate-700'} border`}
                                                >
                                                    {STATUS_LABELS[v.statut] || v.statut}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={(e) => { e.stopPropagation(); openDetail(v); }}
                                                        data-testid={`visite-liste-view-${v.id}`}
                                                    >
                                                        <Eye size={16} />
                                                    </Button>
                                                    {user?.role === 'admin' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                if (!window.confirm(`Supprimer définitivement la visite de ${v.prospect_nom || 'ce prospect'} ? Action irréversible.`)) return;
                                                                try {
                                                                    await axios.delete(`${API}/api/visites/${v.id}`, { withCredentials: true });
                                                                    toast.success('Visite supprimée');
                                                                    fetchVisites();
                                                                } catch (err) {
                                                                    toast.error('Erreur: ' + (err?.response?.data?.detail || err?.message));
                                                                }
                                                            }}
                                                            data-testid={`visite-delete-${v.id}`}
                                                            title="Supprimer (admin)"
                                                        >
                                                            <Trash2 size={16} className="text-red-500" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <VisiteDetailDialog
                open={isDetailOpen}
                onOpenChange={(o) => { setIsDetailOpen(o); if (!o) setDetailVisite(null); }}
                visite={detailVisite}
                config={config}
                onRefresh={fetchVisites}
            />

            <VisiteHistoryDialog
                open={isHistoryOpen}
                onOpenChange={(o) => { setIsHistoryOpen(o); if (!o) setHistoryVisite(null); }}
                visite={historyVisite}
            />
            </ReadOnlyGuard>
        </div>
    );
}
